import { Component, EventEmitter, Output, signal, OnInit, OnDestroy, ElementRef, Renderer2, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ThemeManagementService } from '../../../core/services/theme/theme-management.service';
import { AuthService } from '../../../core/services/Auth/auth-service';
import { ToastService } from '../../services/toast.service';
import { ToastrService } from 'ngx-toastr';
import { Spinner } from "../../../shared/components/spinner/spinner";

@Component({
  selector: 'app-theme-selection-modal',
  standalone: true,
  imports: [CommonModule, Spinner],
  templateUrl: './theme-selection-modal.component.html',
  styleUrls: ['./theme-selection-modal.component.scss']
})
export class ThemeSelectionModalComponent implements OnInit, OnDestroy {
  @Output() close = new EventEmitter<void>();
  @Output() themeSelected = new EventEmitter<string>();
  @Output() themeToggled = new EventEmitter<boolean>();
  @Output() themePreview = new EventEmitter<string>(); // New preview event

  bannerImages = signal<string[]>([]);
  selectedBanner = signal<string>('');

  // Store initial state to revert on cancel
  private initialThemeState: boolean = false;
  private initialSelectedBanner: string = '';

  // Track if we're in preview mode
  private isPreviewMode: boolean = false;

  // Local theme active state for preview mode
  private currentThemeActiveState: boolean = false;

  // Loading state for API operations
  isSaving = signal<boolean>(false);

  // Store original body overflow for restoration
  private originalBodyOverflow: string = '';
  private originalBodyPosition: string = '';
  private readonly toastr = inject(ToastrService);

  constructor(
    private elementRef: ElementRef,
    private renderer: Renderer2,
    private themeManagementService: ThemeManagementService,
    private authService: AuthService,
    private toastService: ToastService
  ) { }

  ngOnInit() {
    // Prevent body scroll when modal is open
    this.preventBodyScroll();

    // Ensure modal is properly positioned
    this.ensureProperPositioning();

    // Generate array of banner image paths from banner1.webp to banner45.webp
    const banners: string[] = [];
    for (let i = 1; i <= 51; i++) {
      banners.push(`assets/images/theme/banner${i}.webp`);
    }
    this.bannerImages.set(banners);

    // Store initial state for potential revert on cancel
    this.initialThemeState = this.isThemeActiveFromStorage();
    this.initialSelectedBanner = localStorage.getItem('selectedBackgroundTheme') || '';

    // Initialize current theme active state
    this.currentThemeActiveState = this.initialThemeState;

    // Load currently active theme if any
    this.loadCurrentActiveTheme();
  }

  ngOnDestroy() {
    // Restore body scroll when modal is destroyed
    this.restoreBodyScroll();
  }

  private preventBodyScroll(): void {
    // Store original overflow values
    this.originalBodyOverflow = document.body.style.overflow || '';
    this.originalBodyPosition = document.body.style.position || '';

    // Prevent background scroll
    this.renderer.setStyle(document.body, 'overflow', 'hidden');
    this.renderer.setStyle(document.body, 'position', 'relative');
  }

  private restoreBodyScroll(): void {
    // Restore original overflow values
    this.renderer.setStyle(document.body, 'overflow', this.originalBodyOverflow);
    this.renderer.setStyle(document.body, 'position', this.originalBodyPosition);
  }

  private ensureProperPositioning(): void {
    // Ensure the modal is properly positioned in the DOM
    const modalElement = this.elementRef.nativeElement;
    if (modalElement) {
      // Reset any inherited transforms that might interfere
      this.renderer.setStyle(modalElement, 'transform', 'none');
      this.renderer.setStyle(modalElement, 'position', 'fixed');
      this.renderer.setStyle(modalElement, 'z-index', '9999');

      // Ensure it covers the full viewport
      this.renderer.setStyle(modalElement, 'top', '0');
      this.renderer.setStyle(modalElement, 'left', '0');
      this.renderer.setStyle(modalElement, 'right', '0');
      this.renderer.setStyle(modalElement, 'bottom', '0');
      this.renderer.setStyle(modalElement, 'width', '100vw');
      this.renderer.setStyle(modalElement, 'height', '100vh');
    }
  }

  private loadCurrentActiveTheme() {
    const savedTheme = localStorage.getItem('selectedBackgroundTheme');
    const isActive = this.isThemeActiveFromStorage();
    if (savedTheme && isActive) {
      this.selectedBanner.set(savedTheme);
      this.currentThemeActiveState = true;
    } else {
      // If no saved theme, ensure state is consistent
      this.currentThemeActiveState = false;
    }
  }

  onClose() {
    // Restore body scroll before closing
    this.restoreBodyScroll();

    // Closing without saving should revert changes like cancel
    this.onCancel();
  }

  onBannerSelect(banner: string) {
    this.selectedBanner.set(banner);

    // Automatically enable theme when a banner is selected
    this.currentThemeActiveState = true;
    this.themeToggled.emit(true);

    // Emit preview event to show the selected theme immediately
    this.themePreview.emit(banner);
    this.isPreviewMode = true;
  }

  onSave() {
    // Check if user is authenticated
    if (!this.authService.isAuthenticated()) {
      console.warn('User not authenticated, saving to localStorage only');
      this.saveToLocalStorage();
      this.closeModal();
      return;
    }

    // Show loading state
    this.isSaving.set(true);
    console.log('Saving theme to API...');

    // Prepare theme data
    const themePath = this.currentThemeActiveState ? this.selectedBanner() : '';
    const isActive = this.currentThemeActiveState;

    // Save to API
    this.themeManagementService.saveUserTheme(themePath, isActive).subscribe({
      next: (response) => {
        if (response.status) {
          // console.log('Theme saved successfully to API:', response);
          this.toastr.success(`Theme saved successfully!`);

          // Emit events for parent components
          if (this.currentThemeActiveState) {
            this.themeSelected.emit(this.selectedBanner());
          } else {
            this.themeSelected.emit('');
          }

          // Show success message (you can add a toast notification here)
          // this.showSuccessMessage('Theme saved successfully!');
        } else {
          // console.error('Failed to save theme:', response.message);
          this.toastr.error(`Failed to save theme. Please try again.`);
          // this.showErrorMessage('Failed to save theme. Please try again.');
        }
      },
      error: (error) => {
        // console.error('Error saving theme to API:', error);
        this.toastr.error(`Network error. Theme saved locally only.`);
        // this.showErrorMessage('Network error. Theme saved locally only.');

        // Fallback to localStorage
        this.saveToLocalStorage();
      },
      complete: () => {
        // Reset loading state
        this.isSaving.set(false);

        // Reset preview mode
        this.isPreviewMode = false;

        // Close modal
        this.closeModal();
      }
    });
  }

  private saveToLocalStorage(): void {
    // Fallback: save to localStorage only
    if (this.currentThemeActiveState) {
      this.themeSelected.emit(this.selectedBanner());
    } else {
      this.themeSelected.emit('');
    }
  }

  private closeModal(): void {
    // Restore body scroll before closing
    this.restoreBodyScroll();

    // Close modal
    this.close.emit();
  }

  private showSuccessMessage(message: string): void {
    this.toastService.showSuccess(message);
  }

  private showErrorMessage(message: string): void {
    this.toastService.showError(message);
  }

  onCancel() {
    // Revert to initial state if we were in preview mode
    if (this.isPreviewMode) {
      // Revert theme state to initial
      this.currentThemeActiveState = this.initialThemeState;
      this.themeToggled.emit(this.initialThemeState);

      // Revert selection to initial
      this.selectedBanner.set(this.initialSelectedBanner);

      // Revert background to initial
      if (this.initialThemeState && this.initialSelectedBanner) {
        // Had a theme initially - restore it
        this.themeSelected.emit(this.initialSelectedBanner);
      } else {
        // No theme initially - clear theme
        this.themeSelected.emit('');
      }
    }

    // Reset preview mode
    this.isPreviewMode = false;

    // Restore body scroll before closing
    this.restoreBodyScroll();

    this.close.emit();
  }

  onThemeToggle(event: Event) {
    const target = event.target as HTMLInputElement;
    const isChecked = target.checked;

    // Update local state
    this.currentThemeActiveState = isChecked;

    // Emit the toggle event
    this.themeToggled.emit(isChecked);

    // If theme is being activated and no banner is selected, select the default
    if (isChecked && !this.selectedBanner()) {
      const defaultTheme = 'assets/images/theme/banner1.webp';
      this.selectedBanner.set(defaultTheme);
      // Emit preview for default theme
      this.themePreview.emit(defaultTheme);
      this.isPreviewMode = true;
    } else if (!isChecked) {
      // Clear selection when theme is deactivated
      this.selectedBanner.set('');
      // Emit empty preview to show time-based background
      this.themePreview.emit('');
      this.isPreviewMode = true;
    } else if (isChecked && this.selectedBanner()) {
      // If theme is activated and banner is already selected, show preview
      this.themePreview.emit(this.selectedBanner());
      this.isPreviewMode = true;
    }

    // Note: Modal should remain open when toggle is changed
    // User must click Save to close the modal
  }

  isThemeActive(): boolean {
    // Always use current state for UI consistency
    return this.currentThemeActiveState;
  }

  isDefaultTheme(banner: string): boolean {
    return banner === 'assets/images/theme/banner1.webp';
  }

  getCurrentTheme(): 'light' | 'dark' {
    return document.documentElement.getAttribute('data-theme') === 'dark' ? 'dark' : 'light';
  }

  /**
   * Modal Closing Behavior:
   * - Modal can ONLY be closed via designated buttons (Close, Cancel, Save)
   * - Backdrop clicks are disabled to prevent accidental closing
   * - Escape key is disabled to force intentional user action
   * - This ensures users must make a conscious decision about theme changes
   */

  private isThemeActiveFromStorage(): boolean {
    return localStorage.getItem('theme-background-active') === 'true';
  }

  onToggleLabelClick(): void {
    // Toggle the current state
    const newState = !this.currentThemeActiveState;
    this.currentThemeActiveState = newState;

    // Emit the toggle event
    this.themeToggled.emit(newState);

    // Handle the toggle logic similar to onThemeToggle
    if (newState && !this.selectedBanner()) {
      // If enabling and no banner selected, select default
      const defaultTheme = 'assets/images/theme/banner1.webp';
      this.selectedBanner.set(defaultTheme);
      this.themePreview.emit(defaultTheme);
      this.isPreviewMode = true;
    } else if (!newState) {
      // Clear selection when theme is deactivated
      this.selectedBanner.set('');
      this.themePreview.emit('');
      this.isPreviewMode = true;
    } else if (newState && this.selectedBanner()) {
      // If enabling and banner is already selected, show preview
      this.themePreview.emit(this.selectedBanner());
      this.isPreviewMode = true;
    }
  }
} 